import React, { useState, useEffect } from 'react';
import { auditService } from '@/shared/services/auditService';
import { AuditLog } from '@/shared/types/audit';
import styles from './AuditLogsPage.module.css';

/**
 * Mapeo de acciones a etiquetas amigables, colores e iconos
 */
const ACTION_MAP: Record<string, { label: string; color: string; icon: string; bg: string }> = {
    UPDATE_SYSTEM_CONFIG: {
        label: 'Ajuste de Configuración',
        color: '#1e40af',
        bg: '#dbeafe',
        icon: '⚙️'
    },
    SETTLEMENT_BATCH_CREATE: {
        label: 'Creación de Lote',
        color: '#6d28d9',
        bg: '#f3e8ff',
        icon: '📁'
    },
    SETTLEMENT_BATCH_CONFIRM: {
        label: 'Confirmación de Lote',
        color: '#b45309',
        bg: '#fef3c7',
        icon: '✅'
    },
    SETTLEMENT_BATCH_MARK_PAID: {
        label: 'Pago de Lote',
        color: '#047857',
        bg: '#d1fae5',
        icon: '💰'
    },
    CASH_HANDOVER_CONFIRM: {
        label: 'Recepción de Efectivo',
        color: '#b45309',
        bg: '#fff7ed',
        icon: '💵'
    },
    BATCH_HANDOVER_CONFIRM: {
        label: 'Recaudación por Lote',
        color: '#92400e',
        bg: '#fef3c7',
        icon: '📦'
    },
    CANCEL_ORDER: {
        label: 'Cancelación de Pedido',
        color: '#991b1b',
        bg: '#fef2f2',
        icon: '🚫'
    },
    OPEN_ORDER_INCIDENT: {
        label: 'Apertura de Incidencia',
        color: '#92400e',
        bg: '#fffbeb',
        icon: '⚠️'
    },
    RESOLVE_ORDER_INCIDENT: {
        label: 'Resolución de Incidencia',
        color: '#1e40af',
        bg: '#eff6ff',
        icon: '🏁'
    },
    SET_USER_ROLE: {
        label: 'Cambio de Rol',
        color: '#1e40af',
        bg: '#e0e7ff',
        icon: '👤'
    },
    APPROVE_RESTAURANT: {
        label: 'Aprobación Comercio',
        color: '#15803d',
        bg: '#dcfce7',
        icon: '✅'
    },
    REJECT_RESTAURANT: {
        label: 'Rechazo Comercio',
        color: '#b91c1c',
        bg: '#fee2e2',
        icon: '❌'
    },
    DISABLE_COUPON: {
        label: 'Cupón Desactivado',
        color: '#7c2d12',
        bg: '#ffedd5',
        icon: '🎟️'
    },
    LOGIN: {
        label: 'Inicio de Sesión',
        color: '#1e293b',
        bg: '#f1f5f9',
        icon: '🔑'
    },
    UPDATE_RESTAURANT: {
        label: 'Perfil Restaurante',
        color: '#be123c',
        bg: '#ffe4e6',
        icon: '🏪'
    }
};

export const AuditLogsPage = () => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const limit = 20;

    useEffect(() => {
        fetchLogs();
    }, [offset]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const response = await auditService.listLogs({ limit, offset });
            setLogs(response.logs);
            setTotal(response.pagination.total);
        } catch (error) {
            console.error('Error fetching audit logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date: any) => {
        if (!date) return 'Sin fecha';

        let d: Date;

        // Caso 1: Objeto con seconds (nuevo nativo o antiguo manual)
        if (date.seconds !== undefined) {
            d = new Date(date.seconds * 1000);
        }
        // Caso 2: Objeto con _seconds (serialización alternativa de Firebase)
        else if (date._seconds !== undefined) {
            d = new Date(date._seconds * 1000);
        }
        // Caso 3: Es una instancia de Date
        else if (date instanceof Date) {
            d = date;
        }
        // Caso 4: Probablemente un ISO string o número
        else {
            d = new Date(date);
        }

        if (isNaN(d.getTime())) return 'Fecha inválida';

        return d.toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getActionInfo = (action: string) => {
        return ACTION_MAP[action] || {
            label: action.replace(/_/g, ' '),
            color: '#475569',
            bg: '#f1f5f9',
            icon: '📝'
        };
    };

    const getInitials = (email: string | undefined): string => {
        if (!email || typeof email !== 'string') return 'AD';
        const namePart = email.split('@')[0];
        return namePart ? namePart.substring(0, 2).toUpperCase() : 'AD';
    };

    const toggleRow = (logId: string) => {
        setExpandedRows(prev => ({
            ...prev,
            [logId]: !prev[logId]
        }));
    };

    const renderJsonDetails = (details: any) => {
        if (!details || typeof details !== 'object') return 'Sin detalles';

        return (
            <div className={styles.jsonGrid}>
                {Object.entries(details).map(([key, value]) => (
                    <div key={key} className={styles.detailItem}>
                        <span className={styles.detailKey}>{key}</span>
                        <div className={styles.detailValue}>
                            {typeof value === 'object'
                                ? JSON.stringify(value).replace(/[{}"]/g, ' ')
                                : String(value)}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Registro de Auditoría</h1>
                <p>Monitoreo profesional de acciones administrativas y cambios de sistema.</p>
            </div>

            <div className={styles.tableContainer}>
                {loading ? (
                    <div className={styles.loading}>
                        <div className="spinner"></div>
                        <p>Analizando registros de sistema...</p>
                    </div>
                ) : (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Fecha y Hora</th>
                                    <th>Administrador</th>
                                    <th>Acción Realizada</th>
                                    <th>Objetivo</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log) => {
                                    const actionInfo = getActionInfo(log.action);
                                    const isExpanded = !!expandedRows[log.logId];

                                    return (
                                        <React.Fragment key={log.logId}>
                                            <tr
                                                className={`${styles.row} ${isExpanded ? styles.rowExpanded : ''}`}
                                                onClick={() => toggleRow(log.logId)}
                                            >
                                                <td style={{ fontWeight: 600 }}>{formatDate(log.createdAt)}</td>
                                                <td>
                                                    <div className={styles.adminCell}>
                                                        <div className={styles.avatar}>
                                                            {getInitials(log.adminEmail || 'AD')}
                                                        </div>
                                                        <div className={styles.adminInfo}>
                                                            <span className={styles.adminEmail}>{log.adminEmail}</span>
                                                            <span className={styles.adminId}>{log.adminId}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span
                                                        className={styles.actionBadge}
                                                        style={{ backgroundColor: actionInfo.bg, color: actionInfo.color }}
                                                    >
                                                        <span className={styles.icon}>{actionInfo.icon}</span>
                                                        {actionInfo.label}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '0.8rem' }}>
                                                        <strong style={{ color: '#64748b', textTransform: 'uppercase' }}>
                                                            {log.targetType}:
                                                        </strong>
                                                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{log.targetId}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span style={{ fontSize: '1.2rem', color: '#94a3b8' }}>
                                                        {isExpanded ? '🔼' : '🔽'}
                                                    </span>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className={styles.expandedRow}>
                                                    <td colSpan={5}>
                                                        <div className={styles.detailsContainer}>
                                                            <div className={styles.detailsTitle}>
                                                                🔍 Desglose Técnico de la Operación
                                                            </div>
                                                            {renderJsonDetails(log.details)}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>

                        <div className={styles.pagination}>
                            <div className={styles.pageInfo}>
                                Mostrando <strong>{offset + 1} - {Math.min(offset + limit, total)}</strong> de <strong>{total}</strong> registros
                            </div>
                            <div className={styles.btnGroup}>
                                <button
                                    className={styles.pageButton}
                                    disabled={offset === 0}
                                    onClick={(e) => { e.stopPropagation(); setOffset(Math.max(0, offset - limit)); }}
                                >
                                    ⬅️ Anterior
                                </button>
                                <button
                                    className={styles.pageButton}
                                    disabled={offset + limit >= total}
                                    onClick={(e) => { e.stopPropagation(); setOffset(offset + limit); }}
                                >
                                    Siguiente ➡️
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
